import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { store } from '../../app/store';
import { ruleCleared } from '../alerts/alertsSlice';
import { DEFAULT_PRESET_MS } from '../rules/replayPresets';
import {
  customWindowCleared,
  customWindowSet,
  fetchStarted,
  paused,
  started,
  stopped,
  windowPicked,
  type RuleStatus,
} from './detectionSlice';

export interface RuleControl {
  status: RuleStatus;
  windowMs: number;
  /** Custom absolute [from, to] window (ISO 8601), or null when using `windowMs`. */
  custom: { from: string; to: string } | null;
  error: string | null;
  lastRunAt: string | null;
  /** How many alerts this rule currently holds. */
  count: number;
  /** Detection is scoped to a project polygon — can't start/fetch without one. */
  canStart: boolean;
  start: () => void;
  pause: () => void;
  stop: () => void;
  fetch: () => void;
  setWindow: (windowMs: number) => void;
  setCustomWindow: (from: string, to: string) => void;
  clearCustomWindow: () => void;
}

/**
 * The per-card control surface. Cards read status/window/count and dispatch
 * lifecycle actions; the engine (mounted once) turns runtime changes into
 * requests. Stop clears the rule's runtime AND its alerts.
 */
export function useRuleControl(ruleId: string): RuleControl {
  const dispatch = useAppDispatch();
  const rt = useAppSelector((s) => s.detection.runtime[ruleId]);
  const count = useAppSelector((s) => s.alerts.byRule[ruleId]?.length ?? 0);
  const canStart = useAppSelector((s) => Boolean(s.projects.selectedId));

  return {
    status: rt?.status ?? 'idle',
    windowMs: rt?.windowMs ?? DEFAULT_PRESET_MS,
    custom: rt?.custom ?? null,
    error: rt?.error ?? null,
    lastRunAt: rt?.lastRunAt ?? null,
    count,
    canStart,
    start: () => {
      // A fresh Start (no cursor yet) begins a clean live run — clear any leftover
      // alerts from a prior forensic Fetch so a historical snapshot doesn't linger
      // and get mixed with live results. A resume-from-pause (cursor set) keeps its
      // accumulated alerts. Read fresh state to avoid a stale render closure.
      if (!store.getState().detection.runtime[ruleId]?.cursorIso) {
        dispatch(ruleCleared(ruleId));
      }
      dispatch(started(ruleId));
    },
    pause: () => dispatch(paused(ruleId)),
    stop: () => {
      dispatch(stopped(ruleId));
      dispatch(ruleCleared(ruleId));
    },
    fetch: () => dispatch(fetchStarted(ruleId)),
    setWindow: (windowMs: number) => dispatch(windowPicked({ ruleId, windowMs })),
    setCustomWindow: (from: string, to: string) =>
      dispatch(customWindowSet({ ruleId, from, to })),
    clearCustomWindow: () => dispatch(customWindowCleared(ruleId)),
  };
}
